/**
 * @file AuthContext.js
 * @description Context provider for authentication state management (User, Login, Logout).
 */
import React, { createContext, useState, useContext, useEffect } from "react";
import { authService } from "../services/authService";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    // Initialize user from localStorage
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem("wiiz_user");
            return stored ? JSON.parse(stored) : null;
        } catch (e) {
            return null;
        }
    });

    const getUserRoleFromToken = (token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            const decoded = JSON.parse(jsonPayload);
            return decoded.role;
        } catch (e) {
            console.error("Failed to decode token", e);
            return "viewer"; // Default fallback
        }
    };

    /**
     * Login function
     * @param {string} username 
     * @param {string} password 
     */
    const login = async (username, password) => {
        try {
            const data = await authService.login(username, password);
            // API usually returns { access_token: "...", ... } or { data: { token: "..." } }
            // Based on curl, it returns a bearer token in header or body?
            // "Authorization: Bearer ..." in curl response usually implies it returns it.
            // Let's assume response JSON contains `access_token` or `token`. 
            // If the API returns the token directly in the body like { "access_token": "..." }:

            const token = data.access_token || data.token || (data.data && data.data.token);

            if (!token) throw new Error("Token not found in response");

            const role = getUserRoleFromToken(token);
            const userData = {
                username, // or data.username
                role,
                token,
                ...data
            };

            setUser(userData);
            localStorage.setItem("wiiz_user", JSON.stringify(userData));
            return userData;
        } catch (error) {
            console.error("Login failed", error);
            throw error;
        }
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem("wiiz_user");
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user }}>
            {children}
        </AuthContext.Provider>
    );
};
